/**
 * Find WebRTC senders and transceivers by media kind, instead of by position.
 *
 * SipClient used to reach for session.connection.getSenders()[0] to get the microphone sender, and assumed every
 * remote 'track' event carried audio for the remote <audio> player. Both assumptions only held while every call
 * had exactly one, audio-only, m-line. Once a call can carry a video m-line too (Task 17's SipVideo), getSenders()[0]
 * can just as well be the video sender depending on transceiver order, and a video track event piped into an
 * <audio> element would play nothing. This module picks senders and transceivers by their actual track kind, so
 * SipClient keeps working regardless of m-line order.
 */

/**
 * Find the local audio sender: the sender of the first transceiver carrying audio, whether that is known already
 * from a remote track received, or from a local track already sending.
 * @param pc The peer connection
 * @returns The audio sender, or undefined if the connection has none
 */
export function audioSender(pc: RTCPeerConnection): RTCRtpSender | undefined {
	return pc.getTransceivers().find(transceiver => transceiver.receiver?.track?.kind === 'audio' || transceiver.sender?.track?.kind === 'audio')?.sender
}

/**
 * Find a video transceiver by label. Stopped transceivers are skipped: they can never carry video again (e.g. the one
 * dropped after a rejected upgrade), and changing one throws.
 * @param pc The peer connection
 * @param label 'camera' (default, same as no label) for the first video transceiver, 'screen' for the second
 * @returns The video transceiver, or undefined if the connection has no such video transceiver
 */
export function videoTransceiver(pc: RTCPeerConnection, label?: 'camera' | 'screen'): RTCRtpTransceiver | undefined {
	const videoTransceivers = pc.getTransceivers().filter(transceiver => transceiver.receiver?.track?.kind === 'video' &&
		transceiver.direction !== 'stopped' && transceiver.currentDirection !== 'stopped')

	return videoTransceivers[label === 'screen' ? 1 : 0]
}

/**
 * Whether a remote 'track' event carries audio, as opposed to video - which SipVideo (Task 17) handles instead
 * @param event The track event
 * @returns True if the event's track is an audio track
 */
export function isAudioTrackEvent(event: RTCTrackEvent): boolean {
	return event.track.kind === 'audio'
}
